import * as React from "react";

import {
    ArrowTrendingLines24Filled,
    ArrowTrendingLines24Regular,
    Bot24Filled,
    Bot24Regular,
    ChartMultiple24Filled,
    ChartMultiple24Regular,
    Clock24Filled,
    Clock24Regular,
    CloudArrowUp24Filled,
    CloudArrowUp24Regular,
    Database24Filled,
    Database24Regular,
    Eye24Filled,
    Eye24Regular,
    Guest24Filled,
    Guest24Regular,
    HeartPulse24Filled,
    HeartPulse24Regular,
    Key24Filled,
    Key24Regular,
    Lightbulb24Filled,
    Lightbulb24Regular,
    LockClosed24Filled,
    LockClosed24Regular,
    MailInbox24Filled,
    MailInbox24Regular,
    Map24Filled,
    Map24Regular,
    PersonAlertOff24Filled,
    PersonAlertOff24Regular,
    Rocket24Filled,
    Rocket24Regular,
    Shield24Filled,
    Shield24Regular,
    SlideSearch24Filled,
    SlideSearch24Regular,
    Table24Filled,
    Table24Regular,
    Wrench24Filled,
    Wrench24Regular
} from "@fluentui/react-icons";

import { TeamAppConfig } from "Apps/TeamAppModel";

const RedirectionApp = React.lazy(async () => {
    const namedExport = await import(/* webpackChunkName: "RedirectionApp" */ "./RedirectionApp");
    return { default: namedExport.RedirectionApp };
});

const WhereIsMyFixRedirect = React.lazy(async () => {
    const namedExport = await import(/* webpackChunkName: "WhereIsMyFixRedirect" */ "../WhereIsMyFix/WhereIsMyFixRedirect");
    return { default: namedExport.WhereIsMyFixRedirect };
});

export const IdentityHub: TeamAppConfig = {
    title: "Identity Hub",
    regularIcon: Guest24Regular,
    filledIcon: Guest24Filled,
    description: "Request or approve Eligibilities and Clearances. Manage IDM approvals. Elevation Approvals and / or change approval team.",
    team: "IDM",
    publicId: "CONVERGEDPRODUCTIONIDENTITYMPA\\IDM",
    owner: "mpaoce@microsoft.com",
    role: "*",
    tags: ["Eligibilities", "clearances", "approvals", "Elevation", "request", "Scenario", "EUPI", "Attestation"],
    app: RedirectionApp,
    path: "/idm/identity/access/Overview",
    metricsPath: "/idm/identity",
    legacy: true
};

export const TransportSLA: TeamAppConfig = {
    title: "Transport SLA",
    regularIcon: ArrowTrendingLines24Regular,
    filledIcon: ArrowTrendingLines24Filled,
    description: "Transport Mailflow Latency Charts",
    team: "Transport",
    publicId: "Exchange\\Transport",
    owner: "extrsladev@microsoft.com",
    role: "*",
    tags: ["Transport", "SLA", "Mailflow", "Latency"],
    app: RedirectionApp,
    path: "/Transport/SLAApp",
    legacy: true
};

export const WhereIsMyFix: TeamAppConfig = {
    title: "Where Is My Fix",
    regularIcon: SlideSearch24Regular,
    filledIcon: SlideSearch24Filled,
    description: "Query your changelist",
    team: "DeploymentCore",
    publicId: "Exchange\\Deployment(Multi-Tenant)",
    owner: "o365deploycore@microsoft.com",
    role: "*",
    tags: ["Build Freshness", "Where Is My Fix"],
    app: WhereIsMyFixRedirect,
    path: "/DeploymentCore/WhereIsMyFixV2App",
    legacy: true,
    // Migrated to the native Where Is My Fix app; kept only so old v1 links redirect there.
    hiddenFromSearch: true
};

export const DeploymentMonitor: TeamAppConfig = {
    title: "Deploy Metric",
    regularIcon: CloudArrowUp24Regular,
    filledIcon: CloudArrowUp24Filled,
    description: "Deployment metrics",
    team: "DeploymentCore",
    publicId: "Exchange\\Deployment(Multi-Tenant)",
    owner: "o365deploycore@microsoft.com",
    role: "*",
    tags: [],
    app: RedirectionApp,
    path: "/DeploymentCore/DeploymentMonitorApp",
    legacy: true
};

export const SecretInventory: TeamAppConfig = {
    title: "Secret Inventory",
    regularIcon: Key24Regular,
    filledIcon: Key24Filled,
    description: "Secret Metadata Inventory",
    team: "Security",
    publicId: "Exchange\\ServiceAuth",
    owner: "o365-service-auth-oc@microsoft.com",
    role: "*",
    tags: ["Security"],
    app: RedirectionApp,
    path: "/Security/SecretInventory",
    legacy: true
};

export const Handoffs: TeamAppConfig = {
    title: "Handoffs",
    regularIcon: PersonAlertOff24Regular,
    filledIcon: PersonAlertOff24Filled,
    description: "Handoffs app provides a list of all the handoffs.",
    team: "OSP",
    publicId: "Exchange\\OSP",
    owner: "OspOncall@microsoft.com",
    role: "*",
    tags: [],
    app: RedirectionApp,
    path: "/handoffs",
    legacy: true
};

export const CMA: TeamAppConfig = {
    title: "CMA",
    regularIcon: Table24Regular,
    filledIcon: Table24Filled,
    description: "Manage Service Incidents and Service Messages for Office365/Dynamics365",
    team: "SCP",
    publicId: "O365SERVICECOMMSPLATFORM\\SCP",
    owner: "ccpscp@microsoft.com",
    role: "*",
    tags: ["Communication Manager Assistant", "SHD"],
    app: RedirectionApp,
    path: "/CustomerKnowledge/CMA",
    legacy: true
};

// ============================================================================
// Phase 1 + 2 — redirect-shim entries for
// v1-only apps. Each marked `legacy: true`; metadata copied from the v1 registries
// to preserve team/owner ownership. Paths must match the actual v1 routes; v1
// default paths follow the `/<TeamFieldValue>/<AppKey>` convention unless an
// explicit `Path:` is set.
//
// Phase 2 addition (TorusDashBoard) follows after manual side-by-side verification
// (2026-05-07) confirmed v2 `Torus` is NOT a replacement for v1 `TorusDashBoard`.
//
// Apps NOT included (and why):
//   - 6 v1 apps with `Envs: []` (no attesting): WatermarkDashboard, Pavc,
//     AdHealth, DirectoryHeatmap, AuxRoleVersions, MonitoringOnboardConfig.
//     They are unreachable in v1 in any environment, so a redirect would 404.
//   - SubstrateHubDashboard: deprecated in v1, already redirects to RuntimeHub
//     v2 there; no v1 destination to redirect to.
//   - M365OpsCenter ("Sovereign Cloud Operations v1"): v2 already has a native
//     `SovCloudOps` app at `/v2/SovCloud` that is a strict superset of v1's
//     M365OpsCenter (adds DashboardParity, DeploymentTracker, ModelGovernance,
//     DataGuardian, DigitalEscort, CloudScope, AcceptanceTestReport). The
//     native v2 entry already covers this surface.
//   - ConfigurableApp: v1 internal/parametric container with
//     `HiddenFromSearch: true, InsightsHidden: true` and `Path: /page/:page`;
//     never user-facing in v1's App Hub.
// ============================================================================

// --- OSP / Core (1) ----------------------------------------------------------

export const SubstrateApp: TeamAppConfig = {
    title: "Substrate App",
    regularIcon: Database24Regular,
    filledIcon: Database24Filled,
    description: "Substrate App",
    team: "OSP",
    publicId: "Exchange\\Monitoring-MaaS",
    owner: "maasfeaturecrew@microsoft.com",
    role: "*",
    tags: ["Substrate", "MaaS"],
    app: RedirectionApp,
    path: "/OSP/SubstrateApp",
    legacy: true
};

// --- Antispam (3) ------------------------------------------------------------

export const MdoIncidentsHub: TeamAppConfig = {
    title: "MDO Incidents Hub",
    regularIcon: Shield24Regular,
    filledIcon: Shield24Filled,
    description: "MDO Incidents Hub of Anti-Spam team",
    team: "Antispam",
    publicId: "EOP\\Antispam-Platform",
    owner: "sz-o365-ip-antispam@microsoft.com",
    role: "*",
    tags: ["SRE", "Assistant", "Incidents"],
    app: RedirectionApp,
    path: "/Antispam/MdoIncidentsHubApp",
    legacy: true
};

export const RuleRolloutDashboard: TeamAppConfig = {
    title: "Anti-Spam Rule Rollout Dashboard",
    regularIcon: Rocket24Regular,
    filledIcon: Rocket24Filled,
    description: "Rule rollout dashboard of Anti-Spam team",
    team: "Antispam",
    publicId: "EOP\\Antispam-Platform",
    owner: "sz-o365-ip-antispam@microsoft.com",
    role: "*",
    tags: ["Rule", "Rollout"],
    app: RedirectionApp,
    path: "/Antispam/RuleRolloutDashboardApp",
    legacy: true
};

export const SvcHealthDashboard: TeamAppConfig = {
    title: "Anti-Spam Health Dashboard",
    regularIcon: HeartPulse24Regular,
    filledIcon: HeartPulse24Filled,
    description: "Service Health Dashboard of Anti-Spam team",
    team: "Antispam",
    publicId: "EOP\\Antispam-Platform",
    owner: "sz-o365-ip-antispam@microsoft.com",
    role: "*",
    tags: ["SHD", "SHM", "Service", "Health"],
    app: RedirectionApp,
    path: "/Antispam/SvcHealthDashboardApp",
    legacy: true
};

// --- Deployment (2) ----------------------------------------------------------

export const BuildFreshness: TeamAppConfig = {
    title: "Build Freshness",
    regularIcon: Clock24Regular,
    filledIcon: Clock24Filled,
    description: "The version distribution of servers in the datacenter",
    team: "DeploymentCore",
    publicId: "Exchange\\Deployment(Multi-Tenant)",
    owner: "o365deploycore@microsoft.com",
    role: "*",
    tags: ["Build", "Freshness", "Deployment"],
    app: WhereIsMyFixRedirect,
    path: "/DeploymentCore/BuildFreshnessApp",
    legacy: true,
    // Migrated to the native Where Is My Fix app; kept only so old v1 links redirect there.
    hiddenFromSearch: true
};

export const DeploymentOCETroubleshooting: TeamAppConfig = {
    title: "Deployment OCE Troubleshooting",
    regularIcon: Wrench24Regular,
    filledIcon: Wrench24Filled,
    description: "Understanding the deployment failure landscape",
    team: "DeploymentCore",
    publicId: "Exchange\\Deployment(Multi-Tenant)",
    owner: "o365deploycore@microsoft.com",
    role: "*",
    tags: ["Deployment", "OCE", "Troubleshooting"],
    app: RedirectionApp,
    path: "/DeploymentCore/DeploymentOCETroubleshootingApp",
    legacy: true
};

// --- DropBox (1) -------------------------------------------------------------

export const OrchestrationHub: TeamAppConfig = {
    title: "Orchestration Hub",
    regularIcon: Lightbulb24Regular,
    filledIcon: Lightbulb24Filled,
    description: "Orchestration Hub",
    team: "DropBox",
    publicId: "Exchange\\CentralAdmin",
    owner: "dropboxteam@service.microsoft.com",
    role: "*",
    tags: ["DropBox", "Orchestration"],
    app: RedirectionApp,
    path: "/DropBox/OrchestrationHub",
    legacy: true
};

// --- FuseBot (2) -------------------------------------------------------------

export const FuseBot: TeamAppConfig = {
    title: "FuseBot",
    regularIcon: Bot24Regular,
    filledIcon: Bot24Filled,
    description: "FuseBot App",
    team: "FuseBot",
    publicId: "Exchange\\FuseBot",
    owner: "FuseBotCore@service.microsoft.com",
    role: "*",
    tags: ["FuseBot"],
    app: RedirectionApp,
    path: "/FuseBot/FuseBotApp",
    legacy: true
};

export const PrAssistant: TeamAppConfig = {
    title: "FuseBot PR Assistant",
    regularIcon: Bot24Regular,
    filledIcon: Bot24Filled,
    description: "FuseBot PR Assistant App",
    team: "FuseBot",
    publicId: "Exchange\\FuseBotPrAssistant",
    owner: "FuseBotCore@service.microsoft.com",
    role: "*",
    tags: ["FuseBot", "PR", "Assistant"],
    app: RedirectionApp,
    path: "/FuseBot/PrAssistant",
    legacy: true
};

// --- Monitoring (1) ----------------------------------------------------------

export const Maas: TeamAppConfig = {
    title: "Observability and Intelligent CloudOps (OIC)",
    regularIcon: Eye24Regular,
    filledIcon: Eye24Filled,
    description: "MaaS App",
    team: "Monitoring",
    publicId: "EXCHANGE\\Monitoring-MaaS",
    owner: "maasoncall@microsoft.com",
    role: "*",
    tags: ["Monitoring - MaaS"],
    app: RedirectionApp,
    path: "/Monitoring/MaasApp",
    legacy: true
};

// --- ServiceAuth (2) ---------------------------------------------------------

export const TorusDashBoard: TeamAppConfig = {
    title: "Torus DashBoard",
    regularIcon: Key24Regular,
    filledIcon: Key24Filled,
    description: "Torus DashBoard",
    team: "ServiceAuth",
    publicId: "Exchange\\ServiceAuth",
    owner: "o365-service-auth-oc@microsoft.com",
    role: "*",
    tags: ["Torus DashBoard"],
    app: RedirectionApp,
    path: "/ServiceAuth/TorusDashBoard",
    legacy: true
};

export const Rbac: TeamAppConfig = {
    title: "RBAC",
    regularIcon: LockClosed24Regular,
    filledIcon: LockClosed24Filled,
    description: "RBAC (Role-Based Access Control)",
    team: "ServiceAuth",
    publicId: "Exchange\\ServiceAuth",
    owner: "OspOncall@microsoft.com",
    role: "*",
    tags: ["RBAC", "ServiceAuth"],
    app: RedirectionApp,
    path: "/ServiceAuth/Rbac",
    legacy: true
};

// --- Transport (4) -----------------------------------------------------------

export const Interceptor: TeamAppConfig = {
    title: "Interceptor Dashboard",
    regularIcon: SlideSearch24Regular,
    filledIcon: SlideSearch24Filled,
    description: "A dashboard for viewing and changing interceptors",
    team: "Transport",
    publicId: "Exchange\\Transport",
    owner: "TSZMFeatureCrew@microsoft.com",
    role: "*",
    tags: ["Transport", "Interceptor"],
    app: RedirectionApp,
    path: "/Transport/InterceptorDashboard",
    legacy: true
};

export const TransportHeatmapV2: TeamAppConfig = {
    title: "Transport Heatmap V2",
    regularIcon: Map24Regular,
    filledIcon: Map24Filled,
    description: "Real Time Transport Heatmap",
    team: "Transport",
    publicId: "Exchange\\Transport",
    owner: "TSZMFeatureCrew@microsoft.com",
    role: "*",
    tags: ["Transport", "Heatmaps"],
    app: RedirectionApp,
    path: "/Transport/TransportHeatmapAppV2",
    legacy: true
};

export const TransportXamProbeHeatmap: TeamAppConfig = {
    title: "Transport XAM Probe Heatmap",
    regularIcon: Map24Regular,
    filledIcon: Map24Filled,
    description: "Transport XAM Probe Heatmap",
    team: "Transport",
    publicId: "Exchange\\Transport",
    owner: "TSZMFeatureCrew@microsoft.com",
    role: "*",
    tags: ["Transport", "XAM", "Probe", "Heatmap"],
    app: RedirectionApp,
    path: "/Transport/TransportXamProbeHeatmapApp",
    legacy: true
};

export const Volume: TeamAppConfig = {
    title: "Transport Volume",
    regularIcon: ArrowTrendingLines24Regular,
    filledIcon: ArrowTrendingLines24Filled,
    description: "Transport Volume Charts",
    team: "Transport",
    publicId: "Exchange\\Transport",
    owner: "TSZMFeatureCrew@microsoft.com",
    role: "*",
    tags: ["Transport", "Volume"],
    app: RedirectionApp,
    path: "/Transport/VolumeApp",
    legacy: true
};

export const TransportQueueLegacy: TeamAppConfig = {
    title: "Transport Queue V2",
    regularIcon: MailInbox24Regular,
    filledIcon: MailInbox24Filled,
    description: "Real Time Delivery Queue Chart V2",
    team: "Transport",
    publicId: "Exchange\\Transport",
    owner: "TSZMFeatureCrew@microsoft.com",
    role: "*",
    tags: ["Transport", "Queue"],
    app: RedirectionApp,
    path: "/Transport/QueueAppV2",
    legacy: true
};

export const TransportReportingLegacy: TeamAppConfig = {
    title: "Transport Reporting",
    regularIcon: ChartMultiple24Regular,
    filledIcon: ChartMultiple24Filled,
    description: "Transport Historical Reporting",
    team: "Transport",
    publicId: "Exchange\\Transport",
    owner: "extrsladev@microsoft.com",
    role: "*",
    tags: ["Transport", "Historical", "Reporting"],
    app: RedirectionApp,
    path: "/Transport/TransportReportingApp",
    legacy: true
};
